<template>
  <v-row class="body-2 ticket-action-buttons" no-gutters>
    <v-col cols="auto" class="pr-2 d-flex align-center">
      <span class="grey--text text--darken-3">Priority</span>
      <v-menu offset-y class="cursor-pointer" attach>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            :disabled="permissionCheck"
            :ripple="false"
            plain
            class="justify-space-between text-capitalize body-1 opacity-none px-2"
            min-width="50px"
          >
            <span>
              <v-icon :color="priorityColor(ticketDetails.priority)" size="10px">mdi-checkbox-blank-circle</v-icon>
               {{ ticketDetails.priority | upperCaseFirst }} 
            </span>
            <div>
              <v-icon v-if="attrs['aria-expanded'] == 'false'">
                mdi-menu-down
              </v-icon>
              <v-icon v-if="attrs['aria-expanded'] == 'true'">
                mdi-menu-up
              </v-icon>
            </div>
          </v-btn>
        </template>
        <v-list>
          <v-list-item-group>
            <v-list-item
              class="cursor-pointer"
              v-track="'update-priority'"
              v-for="(priority, index) in associatedField.priorities"
              :key="index"
              @click="updateSingleTicket(ticketDetails,'priority',priority.name,'Priority')"
              :class="activeListClass(ticketDetails,'priority',priority,'name')">
              <v-list-item-title class="text-capitalize"> 
                <v-icon :color="priorityColor(priority.name)" size="10px" class="pr-1">mdi-checkbox-blank-circle</v-icon>
                {{ priority.name }}</v-list-item-title>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-menu>
    </v-col>
    <v-col cols="auto" class="px-2 d-flex align-center">
      <div class="grey--text text--darken-3">Type</div>
      <v-menu
        offset-y
        class="cursor-pointer"
        z-index="999"
        content-class="scroll-dropdown"
        attach
        max-width="260px"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            :disabled="permissionCheck"
            class="justify-space-between text-capitalize body-1 opacity-none px-2"
            @click="identifyFields('ticket_types')"
            :ripple="false"
            plain
            min-width="50px"
          >
            <span class="text-truncate d-block action-menu-width">{{
              !ticketDetails.ticket_type_name
                ? "--"
                : ticketDetails.ticket_type_name | upperCaseFirst
            }}</span>
            <div>
              <v-icon v-if="attrs['aria-expanded'] == 'false'">
                mdi-menu-down
              </v-icon>

              <v-icon v-if="attrs['aria-expanded'] == 'true'">
                mdi-menu-up
              </v-icon>
            </div>
          </v-btn>
        </template>
        <v-list
          class="py-0 action-dropdown overflow-y-auto"
          v-scroll.self="indexItemsScroll"
        >
          <v-list-item-group>
            <v-list-item
              v-if="ticketDetails.ticket_type_name"
              @click="unAssign('type')"
              block
              v-track="'unassign-type'"
              class="unassign-btn subtitle-1 primary--font"
            >
              Unassign
            </v-list-item>
            <v-list-item
              class="cursor-pointer"
              v-track="'update-ticket-type'"
              v-for="(type, index) in filteredTypesList"
              :key="index"
              @click="updateSingleTicket(ticketDetails,'ticket_type_id',type.id,'Type')"
              :class="activeListClass(ticketDetails,'ticket_type_name',type,'name')">
              <v-list-item-title>{{ type.name }}</v-list-item-title>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-menu>
    </v-col>

     <v-col cols="auto" class="px-2 d-flex align-center">
      <div class="grey--text text--darken-3"> Status </div>
      <v-menu offset-y class="cursor-pointer float-left">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            plain
            :ripple="false"
            :disabled="ticketAccess.closed || !ticketAccess.permissions.create || ticketAccess.deleted"           
            class="justify-space-between text-capitalize body-1 px-2 opacity-none"
            attach
          >
            <span>
                {{ ticketDetails.status === "on_hold"
                  ? "On hold"
                  : ticketDetails.status | upperCaseFirst
                }}
            </span>
            <div>
              <v-icon v-if="attrs['aria-expanded'] == 'false'">
                mdi-menu-down
              </v-icon>
              <v-icon v-if="attrs['aria-expanded'] == 'true'">
                mdi-menu-up
              </v-icon>
            </div>
          </v-btn>
        </template>
        <v-list>
          <v-list-item-group>
            <v-list-item
              class="cursor-pointer"
              v-track="'update-ticket-status'"
              v-for="(status, index) in filterStatusList"
              :key="index"
              @click="updateSingleTicket(ticketDetails,'status',status.name,'Status')"
              :class="activeListClass(ticketDetails,'status',status,'name')">
              <v-list-item-title v-if="status.name">
                {{ status.name.replace("_", " ") }}</v-list-item-title
              >
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-menu>
    </v-col>

    <v-col cols="auto" class="px-2 d-flex align-center">
      <div class="grey--text text--darken-3"> Agent </div>
      <v-menu
        offset-y
        class="cursor-pointer float-left"
        :close-on-content-click="hideAgentDropdown"
        attach
        plain
        max-width="250px"
        >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            :disabled="permissionCheck"
            :ripple="false"
            plain
            class="justify-space-between text-capitalize body-1 opacity-none px-2"
            min-width="55px"
          > 
            <span class="text-truncate d-block action-menu-width">
              {{
                !ticketDetails.agent_id
                  ? "--"
                  : ticketDetails.agent_name | upperCaseFirst
              }}
            </span>
            <div>
                <v-icon v-if="attrs['aria-expanded'] == 'false'">
                  mdi-menu-down
                </v-icon>
                <v-icon v-if="attrs['aria-expanded'] == 'true'">
                  mdi-menu-up
                </v-icon>
            </div>
          </v-btn>
        </template>

        <v-list
          class="action-dropdown overflow-y-auto"
        >
          <v-list-item @click="hideAgentDropdown = false" class="pa-0">
            <v-text-field
              class="search-agent px-2"
              v-track="'get-agent-list'"
              hide-details
              autofocus
              v-model="enteredAgent"
              @input="getAgentList(enteredAgent)"
            ></v-text-field>
          </v-list-item>
          <v-list-item-group>
            <v-list-item
              v-if="ticketDetails.agent_id"
              @click="unAssign('agent')"
              v-track="'unassign-agent'"
              block
              class="unassign-btn subtitle-1 primary--font"
            >
              Unassign
            </v-list-item>
            <template
              v-if="
                associatedField.agentList && associatedField.agentList.length
              "
            >
              <v-list-item
                class="cursor-pointer"
                v-track="'update-agent'"
                v-for="agent in filteredAgentList"
                :key="agent.id"
                @click="updateSingleTicket(ticketDetails,'agent_id',agent.id,'Agent')"
                :class="activeListClass(ticketDetails,'agent_name',agent,'agent_name')">
                <v-list-item-title>{{ agent.agent_name }}</v-list-item-title>
              </v-list-item>
            </template>
            <v-list-item v-else>
              <v-list-item-title> No data found ! </v-list-item-title>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-menu>
    </v-col>
  </v-row>
</template>
<script>
import { ticketMixin } from "@/mixins/ticket";
export default {
  name: "Ticket-actions",
  mixins: [ticketMixin],
  props: {
    ticketDetails: {
      type: Object,
    },
  },
  data() {
    return {
      hideAgentDropdown: false,
      enteredAgent: "",
    };
  },
  computed: {
    associatedField() {
      return this.$store.getters["tickets/associatedField"];
    },
    filterStatusList() {
      return this.associatedField.statusList.filter(status => status.name !== "new");
    },
    permissionCheck () {
      return this.ticketAccess.closed || !this.ticketAccess.permissions.create || this.ticketAccess.deleted
    }
  },
  methods: {
    unAssign(action) {
      switch (action) {
        case "agent":
          this.updateSingleTicket(
            this.ticketDetails,
            "agent_id",
            null,
            "Agent"
          );
          break;
        case "type":
          this.updateSingleTicket(
            this.ticketDetails,
            "ticket_type_id",
            null,
            "Type"
          );
          break;
        default:
          break;
      }
    },

    updateSingleTicket(ticket, name, value, actionName) {
      if(actionName === 'Agent'){
        this.hideAgentDropdown = true;
        this.enteredAgent = null;
      }
      this.$emit("updateTicket",ticket, name, value, actionName);
    },  
  },
};
</script>

<style lang="scss">
.ticket-action-buttons {
  .action-dropdown{
    max-height: 290px;
  }
  .action-menu-width{
    max-width: 200px;
  }
  .visibility-hidden{
    visibility: hidden;
  }
  .opacity-none .v-btn__content {
    opacity: inherit !important
  }
}
</style>